import { Assistant, Campaign, Close, LiveHelpOutlined } from '@mui/icons-material';
import { Badge, BottomNavigation, BottomNavigationAction, Box, styled } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import type { CSSObject, Theme } from '@mui/material/styles';
import { useRef, useState, type TransitionEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AIChat } from '~/components/AIChat/AIChat';
import { IconButton } from '~/components/IconButton';
import { Typography } from '~/components/Typography';
import { AnnouncementList } from '~/components/announcements/AnnouncementList';
import { useAppContext } from '~/contexts';
import { useSideNav } from '../context';

const drawerWidth = 340;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create(['width', 'visibility'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  visibility: 'visible',
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    zIndex: '1200',
  },
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create(['width', 'visibility'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  // Width 0 only clips the panel; hidden also takes its controls out of the tab order.
  visibility: 'hidden',
  width: 0,
  [theme.breakpoints.down('sm')]: {
    width: 0,
    position: 'fixed',
    zIndex: '1200',
  },
});

export const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

export const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  maxHeight: '64px',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

type PanelTab = 'assistant' | 'announcements';

export const SidePanel = () => {
  const { rightPanelOpen, setRightPanelOpen, toggleRightPanel } = useSideNav();
  const ref = useRef<HTMLDivElement>(null);
  const helpTabRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();

  // From AppContext, which polls every 30s and is already loaded, so the badge costs nothing
  // and never fetches a body.
  const { unreadAnnouncementCount } = useAppContext();

  const [tab, setTab] = useState<PanelTab>('assistant');

  return (
    <>
      <Box
        component="button"
        type="button"
        ref={helpTabRef}
        aria-expanded={rightPanelOpen}
        onClick={() => {
          setTab('assistant');
          toggleRightPanel();
        }}
        sx={(theme) => ({
          border: 0,
          font: 'inherit',
          padding: 0,
          position: 'fixed',
          top: '50%',
          right: 0,
          transform: 'translateY(-50%)',
          zIndex: theme.zIndex.drawer - 1,
          bgcolor: 'primary.dark',
          color: 'common.white',
          width: 30,
          height: 96,
          borderRadius: '8px 0 0 8px',
          boxShadow: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexDirection: 'column',
          // Flush with the viewport edge, which would cut off an outset ring.
          '&:focus-visible': theme.mixins.focusRingInset,
        })}
      >
        <Badge
          // An invisible zero badge still renders its "0", which would join the button's name.
          aria-hidden={unreadAnnouncementCount === 0 || undefined}
          badgeContent={unreadAnnouncementCount}
          color="error"
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          // Opens rather than toggles: the count is an invitation to read, so it should never
          // close an already-open panel.
          onClick={(e) => {
            e.stopPropagation();
            setTab('announcements');
            setRightPanelOpen(true);
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
        <LiveHelpOutlined />
        <Box
          sx={{
            mt: 1,
            writingMode: 'vertical-rl',
            fontWeight: 600,
          }}
        >
          {t('components.ai_chat.help_tab')}
        </Box>
      </Box>
      <Drawer
        variant="permanent"
        open={rightPanelOpen}
        anchor="right"
        PaperProps={{
          sx: {
            backgroundColor: 'primary.main',
            border: 0,
            overflow: 'hidden',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
          onTransitionEnd: (event: TransitionEvent<HTMLDivElement>) => {
            // Focus and hover transitions inside the panel bubble here; only the open animation counts.
            if (event.target !== event.currentTarget || event.propertyName !== 'width') return;
            if (!rightPanelOpen || tab !== 'assistant') return;
            ref.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus();
          },
        }}
      >
        <DrawerHeader>
          <Box sx={{ color: 'common.white', display: 'flex', p: 1 }}>
            {tab === 'assistant' ? <Assistant /> : <Campaign />}
          </Box>
          <Typography
            sx={{
              color: 'primary.mutedText',
              fontSize: '1rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {tab === 'assistant'
              ? t('components.ai_chat.ai_tab')
              : t('components.announcements.title')}
          </Typography>
          <IconButton
            label={t('common.actions.close')}
            sx={{
              color: 'common.white',
            }}
            onClick={() => {
              toggleRightPanel();
              // The panel hides once closed, which would otherwise drop focus to the page.
              helpTabRef.current?.focus();
            }}
          >
            {<Close />}
          </IconButton>
        </DrawerHeader>
        <Box
          ref={ref}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Kept mounted rather than swapped out, so switching tabs does not throw away an
              in-progress conversation. */}
          <Box
            sx={{
              display: tab === 'assistant' ? 'flex' : 'none',
              flexDirection: 'column',
              flexGrow: 1,
              minHeight: 0,
            }}
          >
            <AIChat />
          </Box>
          {/* Keyed on the unread count so a new announcement remounts this and refetches. */}
          {tab === 'announcements' && <AnnouncementList key={unreadAnnouncementCount} />}
        </Box>

        <BottomNavigation
          value={tab}
          onChange={(_, value: PanelTab) => setTab(value)}
          showLabels
          sx={{
            bgcolor: 'background.default',
            borderTop: '1px solid',
            borderColor: 'divider',
            // After borderColor, which is a shorthand and would otherwise recolour this.
            borderLeft: 1,
            borderLeftColor: 'borders.main',
            flexShrink: 0,
            // Matches TablePagination's toolbar, so the tab bar lines up with the
            // pagination bar on list pages beside it.
            height: 52,
            // MUI grows the label on selection, which makes it jump.
            '& .MuiBottomNavigationAction-label, & .MuiBottomNavigationAction-label.Mui-selected': {
              fontSize: '0.75rem',
            },
            // BottomNavigation has no indicator of its own. Scoped to the action, because
            // Mui-selected lands on the label too and that renders a second, text-width bar.
            '& .MuiBottomNavigationAction-root.Mui-selected': {
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 3,
                bgcolor: 'primary.dark',
              },
            },
          }}
        >
          <BottomNavigationAction
            value="assistant"
            icon={<Assistant />}
            label={t('components.ai_chat.ai_tab')}
          />
          <BottomNavigationAction
            value="announcements"
            icon={
              <Badge badgeContent={unreadAnnouncementCount} color="error">
                <Campaign />
              </Badge>
            }
            label={t('components.announcements.tab')}
          />
        </BottomNavigation>
      </Drawer>
    </>
  );
};
